import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Calendar, Bus, CreditCard, AlertTriangle, Shield, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';

const WeekendTermsPage = () => {
  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      {/* Header */}
      <div className="bg-white/80 border-b border-stone-200 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/weekends" className="inline-flex items-center text-violet-600 hover:text-violet-700 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Weekends
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Title Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-violet-100 rounded-full mb-4">
            <FileText className="w-8 h-8 text-violet-600" />
          </div>
          <h1 className="text-3xl font-bold text-stone-900 mb-2">Weekend Events</h1>
          <h2 className="text-xl text-violet-600 mb-2">Booking Terms & Conditions</h2>
          <p className="text-stone-500">LINE DANCE CENTRAL CIC</p>
        </div>

        {/* Quick Reference Box */}
        <div className="bg-violet-50 border border-violet-200 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center">
            <AlertTriangle className="w-5 h-5 text-amber-600 mr-2" />
            Important Booking Information
          </h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <p className="text-stone-600"><strong className="text-stone-900">Weekend deposit:</strong> £35 per person – non-refundable where the customer cancels</p>
              <p className="text-stone-600"><strong className="text-stone-900">Optional coach deposit:</strong> £15 per person – non-refundable where the customer cancels</p>
              <p className="text-stone-600"><strong className="text-stone-900">Instalments:</strong> Monthly instalments normally available</p>
            </div>
            <div className="space-y-2">
              <p className="text-stone-600"><strong className="text-stone-900">Final balance:</strong> Due no later than 6 weeks before the weekend unless otherwise stated</p>
              <p className="text-stone-600"><strong className="text-stone-900">Twin/Double rooms:</strong> Prices are based upon shared occupancy</p>
              <p className="text-stone-600"><strong className="text-stone-900">Insurance:</strong> We strongly recommend appropriate travel/event cancellation insurance</p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="prose prose-violet max-w-none mb-8">
          <p className="text-stone-600 leading-relaxed">
            These Booking Terms & Conditions apply to weekend events organised by Line Dance Central CIC ("Line Dance Central", "LDC", "we", "us" or "our"), including optional coach travel where offered.
          </p>
          <p className="text-stone-600 leading-relaxed">
            Please read these terms carefully before booking.
          </p>
          <p className="text-stone-600 leading-relaxed">
            By making a booking and paying the required deposit, the person making the booking ("Lead Booker") confirms that they have read and accepted these terms on behalf of themselves and all persons included within their booking.
          </p>
        </div>

        {/* Terms Sections */}
        <div className="space-y-8">
          {/* Section 1 */}
          <Section number={1} title="Making a Booking" icon={<Calendar className="w-5 h-5" />}>
            <p>All bookings are subject to availability.</p>
            <p>The Lead Booker must be aged 18 or over and is responsible for:</p>
            <ul>
              <li>providing accurate booking information;</li>
              <li>making payments by the required dates;</li>
              <li>informing us of relevant changes to the booking; and</li>
              <li>ensuring everyone included within the booking is aware of these Booking Terms & Conditions.</li>
            </ul>
            <p>A place is not secured until Line Dance Central has received the required deposit.</p>
          </Section>

          {/* Section 2 */}
          <Section number={2} title="Weekend Deposit" icon={<CreditCard className="w-5 h-5" />}>
            <p>A £35 deposit per person is required to secure a place on a Line Dance Central Weekend.</p>
            <p>The £35 deposit is a non-refundable reservation payment and forms part of the total weekend price.</p>
            <p>For example, where a weekend costs £225 per person:</p>
            <div className="bg-stone-50 rounded-lg p-4 my-4">
              <ul className="list-none space-y-1">
                <li><span className="text-stone-500">Weekend price:</span> <span className="text-stone-900 font-medium">£225</span></li>
                <li><span className="text-stone-500">Deposit:</span> <span className="text-stone-900 font-medium">£35</span></li>
                <li><span className="text-stone-500">Remaining balance:</span> <span className="text-stone-900 font-medium">£190</span></li>
              </ul>
            </div>
            <p>The deposit secures the guest's place and contributes towards the administrative and financial commitments Line Dance Central makes when accepting the booking.</p>
            <p>Where a customer chooses to cancel, the £35 deposit will not normally be refunded.</p>
            <p>This does not affect circumstances where Line Dance Central cancels the event or where a refund is otherwise required by law.</p>
          </Section>

          {/* Section 3 */}
          <Section number={3} title="Optional Coach Travel" icon={<Bus className="w-5 h-5" />}>
            <p>For selected weekends, Line Dance Central may offer optional coach travel.</p>
            <p>Coach travel is an additional extra and is not included within the advertised weekend price unless specifically stated.</p>
            <p>Where coach travel is selected, a separate <strong>£15 deposit per person</strong> is required to secure the coach place.</p>
            <p>The £15 coach deposit is a non-refundable reservation payment and forms part of the total coach fare.</p>
            <p>Therefore, where a guest books both the weekend and coach travel, the initial payment will normally comprise:</p>
            <div className="bg-stone-50 rounded-lg p-4 my-4">
              <p className="text-stone-900 font-medium">£35 Weekend Deposit + £15 Coach Deposit = £50 per person</p>
            </div>
            <p>The weekend and coach deposits are separate and relate to different elements of the booking.</p>
          </Section>

          {/* Section 4 */}
          <Section number={4} title="Payment of the Balance">
            <p>The remaining balance may be paid in full or through an instalment plan where offered.</p>
            <p>Line Dance Central normally offers monthly instalment plans.</p>
            <p>The exact payment schedule will be confirmed when the booking is made.</p>
            <p>Where somebody books after the standard instalment schedule has commenced, a shorter instalment arrangement may be offered.</p>
            <p>The number and amount of instalments may therefore vary depending upon when the booking is made.</p>
            <p>Unless Line Dance Central confirms otherwise, the full balance must be received no later than <strong>six weeks before the start of the weekend</strong>.</p>
            <p>This deadline is necessary because our accommodation provider normally requires Line Dance Central to make full payment approximately one month before the event.</p>
            <p>Where coach travel is selected, the coach balance may be incorporated into the customer's instalment schedule or collected separately.</p>
            <p className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
              An instalment arrangement is a facility allowing the booking price to be paid over time. It is not a monthly subscription and stopping an instalment arrangement does not automatically cancel the underlying booking or remove any cancellation liability.
            </p>
          </Section>

          {/* Section 5 */}
          <Section number={5} title="Missed or Late Payments">
            <p>Customers are responsible for making payments by the agreed dates.</p>
            <p>If an instalment is missed, Line Dance Central will normally contact the Lead Booker and provide a reasonable opportunity for the account to be brought up to date.</p>
            <p>If payment remains outstanding following reasonable notice, Line Dance Central may cancel the booking.</p>
            <p>Where Line Dance Central cancels because the customer has failed to make the agreed payments, this will normally be treated as a cancellation by the customer and the cancellation provisions below will apply.</p>
            <p>Any amount retained or remaining payable will be limited to Line Dance Central's reasonable unrecoverable losses arising from the cancellation.</p>
          </Section>

          {/* Section 6 */}
          <Section number={6} title="Cancellation by the Customer">
            <p>All cancellations must be made in writing by the Lead Booker.</p>
            <p>The effective cancellation date will be the date on which Line Dance Central receives the written cancellation.</p>
            <p>The £35 weekend deposit per person is non-refundable where the customer chooses to cancel.</p>
            <p>Line Dance Central makes financial commitments to hotels, venues, entertainers and other suppliers well in advance of an event. These commitments generally increase as the weekend approaches.</p>
            <p>Our normal cancellation arrangements are therefore:</p>
            
            <div className="space-y-4 my-4">
              <CancellationTier 
                period="More than 180 days before the event"
                description="The £35 deposit will be retained. Other weekend payments will normally be refunded unless Line Dance Central has already incurred additional non-recoverable costs directly associated with the booking."
              />
              <CancellationTier 
                period="120–180 days before the event"
                description="The £35 deposit will be retained together with any additional amount reasonably required to cover costs or losses already incurred by Line Dance Central which cannot be recovered."
              />
              <CancellationTier 
                period="60–119 days before the event"
                description="The £35 deposit will be retained. Line Dance Central may also retain an appropriate proportion of other payments where necessary to cover financial commitments and reasonable losses resulting from the cancellation."
              />
              <CancellationTier 
                period="31–59 days before the event"
                description="The £35 deposit will be retained. A substantial proportion of payments may also become non-refundable where Line Dance Central has become financially committed to accommodation, entertainment or other event costs."
              />
              <CancellationTier 
                period="30 days or less before the event"
                description="Our accommodation provider normally requires Line Dance Central to pay accommodation charges in full approximately one month before the event. Once Line Dance Central has become liable for or has paid these charges, up to the full weekend price may become non-refundable and/or remain payable where those costs cannot be recovered."
                warning="Cancellation within 30 days of the weekend is therefore likely to result in little or no refund being available."
              />
            </div>

            <p>Where the customer has not yet paid their full balance but Line Dance Central has already become contractually liable for costs associated with that guest, the customer may remain responsible for the outstanding amount representing Line Dance Central's reasonable unrecoverable loss.</p>
            
            <h4 className="text-stone-900 font-semibold mt-6 mb-2">Reducing Our Loss</h4>
            <p>Line Dance Central will take reasonable steps to reduce its losses following a cancellation.</p>
            <p>Where we are able to:</p>
            <ul>
              <li>resell the cancelled place;</li>
              <li>obtain a refund from a supplier;</li>
              <li>avoid an accommodation charge; or</li>
              <li>otherwise reduce the financial loss,</li>
            </ul>
            <p>this will be taken into account when calculating the final cancellation charge.</p>
            <p>Cancellation charges will not exceed Line Dance Central's reasonable loss resulting from the cancellation.</p>
          </Section>

          {/* Section 7 */}
          <Section number={7} title="Cancellation Due to Illness or Personal Circumstances">
            <p>Illness, injury, bereavement, changes in personal circumstances, work commitments, transport difficulties or an inability to attend do not automatically entitle a guest to a refund outside the cancellation provisions above.</p>
            <p className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-violet-800">
              For this reason, we strongly recommend appropriate travel/event cancellation insurance from the date of booking.
            </p>
          </Section>

          {/* Section 8 */}
          <Section number={8} title="Transferring a Weekend Booking">
            <p>Where a guest is unable to attend, Line Dance Central may permit the booking to be transferred to another person.</p>
            <p>Transfers must be requested in writing and approved by Line Dance Central.</p>
            <p>The replacement guest must:</p>
            <ul>
              <li>accept these Booking Terms & Conditions;</li>
              <li>provide all information reasonably required for the booking; and</li>
              <li>pay any outstanding balance.</li>
            </ul>
            <p>Any additional charge imposed by the hotel, coach company or another supplier as a result of the change will be payable by the customer.</p>
            <p>Line Dance Central may impose a reasonable deadline for name changes where required by our suppliers.</p>
            <p>Where a replacement guest takes over the booking, this will be taken into account when determining any financial loss arising from the original cancellation.</p>
          </Section>

          {/* Section 9 */}
          <Section number={9} title="Twin and Shared Room Bookings">
            <p>Where two guests book a twin or double room, the advertised price is based upon two people occupying that room.</p>
            <p>If one guest subsequently cancels, this does not automatically cancel the booking of the remaining guest.</p>
            <p>If the hotel charges Line Dance Central a:</p>
            <ul>
              <li>single occupancy supplement;</li>
              <li>under-occupancy charge; or</li>
              <li>revised room rate</li>
            </ul>
            <p>because one guest has cancelled, the remaining guest will be responsible for paying that additional charge.</p>
            <p>This applies even if the remaining guest has already paid their original weekend balance in full.</p>
            <p>Line Dance Central will notify the remaining guest of the additional amount as soon as reasonably practicable.</p>
            <p>Where possible, Line Dance Central may offer an alternative which avoids or reduces the additional charge. This could include:</p>
            <ul>
              <li>a replacement guest taking over the cancelled place;</li>
              <li>moving the guest to another suitable room; or</li>
              <li>sharing with another guest who is also looking for a room-share.</li>
            </ul>
            <p>Any room-sharing arrangement will only be made with the agreement of the guests concerned.</p>
            <p>Line Dance Central is not responsible for finding a replacement roommate.</p>
            <p>If no suitable alternative is available or agreed, the remaining guest will be responsible for the additional accommodation charge imposed upon Line Dance Central by the hotel.</p>
            <p>If the remaining guest decides to cancel rather than pay the additional single occupancy or under-occupancy charge, their cancellation will be treated as a customer cancellation and the normal cancellation provisions will apply.</p>
            <p>If the hotel does not impose an additional charge, Line Dance Central will not impose a single occupancy charge of its own.</p>
          </Section>

          {/* Section 10 */}
          <Section number={10} title="Cancellation of Coach Travel">
            <p>The £15 coach deposit per person is non-refundable where the customer chooses to cancel their coach place.</p>
            <p>Coach transport involves Line Dance Central committing to a particular vehicle, passenger capacity and cost.</p>
            <p>Additional coach payments may therefore also become wholly or partly non-refundable where Line Dance Central has incurred costs with the coach operator that cannot reasonably be recovered.</p>
            <p>Where a cancelled coach place can be resold or Line Dance Central is otherwise able to reduce its loss, this will be taken into account when determining any refund.</p>
            <p>Cancellation of coach travel does not automatically cancel the weekend booking.</p>
            <p>Likewise, cancellation of the weekend does not automatically make coach payments refundable. The coach element will be assessed separately according to the costs and losses associated with the coach booking.</p>
          </Section>

          {/* Section 11 */}
          <Section number={11} title="Coach Arrangements">
            <p>Coach departure locations and approximate departure times will be communicated to passengers before travel.</p>
            <p>Passengers are responsible for arriving at the stated collection point in sufficient time.</p>
            <p className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
              The coach cannot normally be held for late passengers. If a passenger misses the coach because they arrive after the stated departure time, they will be responsible for arranging and paying for alternative transport. No refund of the coach fare will normally be available in these circumstances.
            </p>
            <p>The same principle applies to the return journey.</p>
            <p>Passengers must comply with reasonable instructions given by the coach driver, coach operator and Line Dance Central representatives.</p>
            <p>Line Dance Central may make reasonable operational changes to departure times, collection points, routes or vehicles where necessary.</p>
          </Section>

          {/* Section 12 */}
          <Section number={12} title="Coach Delays">
            <p>Journey and arrival times are estimates.</p>
            <p>Journeys may be affected by traffic, accidents, weather, road closures, breakdowns or other circumstances outside Line Dance Central's reasonable control.</p>
            <p>Line Dance Central will not be responsible for unavoidable delays caused by circumstances outside its reasonable control or for consequential costs arising from such delays, except where liability cannot legally be excluded.</p>
          </Section>

          {/* Section 13 */}
          <Section number={13} title="Coach Luggage">
            <p>Passengers must comply with any luggage restrictions communicated for the journey.</p>
            <p>The coach operator may restrict excessive or unusually large luggage where necessary because of vehicle capacity or safety requirements.</p>
            <p>Passengers are responsible for ensuring that their belongings are loaded onto and removed from the coach.</p>
          </Section>

          {/* Section 14 */}
          <Section number={14} title="Cancellation of Coach Travel by Line Dance Central">
            <p>Coach travel may be dependent upon sufficient passenger numbers.</p>
            <p>Line Dance Central reserves the right to withdraw the coach option where insufficient bookings are received to make the service reasonably viable.</p>
            <p>Where Line Dance Central cancels the coach service, customers will receive a refund of the coach payments they have made, including the £15 coach deposit.</p>
            <p>Cancellation of the coach service does not automatically cancel the customer's weekend booking.</p>
            <p>The customer will remain responsible for making their own travel arrangements to and from the event unless Line Dance Central confirms alternative arrangements.</p>
            <p>Where practicable, Line Dance Central may offer alternative transport arrangements instead of cancelling the service.</p>
          </Section>

          {/* Section 15 */}
          <Section number={15} title="Failure to Attend or Early Departure">
            <p>Where a guest:</p>
            <ul>
              <li>fails to attend;</li>
              <li>arrives late;</li>
              <li>leaves the weekend early; or</li>
              <li>chooses not to use accommodation, meals, entertainment, dancing, coach travel or another service included within their booking,</li>
            </ul>
            <p>no refund will normally be payable for services which remain available to them.</p>
            <p>This does not affect the customer's statutory rights.</p>
          </Section>

          {/* Section 16 */}
          <Section number={16} title="Changes to the Event">
            <p>Line Dance Central may make reasonable changes to the event programme where necessary.</p>
            <p>This may include changes to:</p>
            <ul>
              <li>instructors;</li>
              <li>choreographers;</li>
              <li>DJs;</li>
              <li>entertainers;</li>
              <li>activities;</li>
              <li>running orders;</li>
              <li>class times; or</li>
              <li>other individual elements of the programme.</li>
            </ul>
            <p>Advertised instructors, choreographers, DJs and entertainers are subject to availability.</p>
            <p>The replacement or non-appearance of an individual instructor, choreographer, DJ or entertainer will not normally constitute cancellation of the weekend where the overall nature of the event remains substantially as advertised.</p>
            <p>Where a change fundamentally alters the nature of the service booked, any rights and remedies available to the customer under applicable law will continue to apply.</p>
          </Section>

          {/* Section 17 */}
          <Section number={17} title="Cancellation of the Weekend by Line Dance Central">
            <p>If Line Dance Central cancels the entire weekend and cannot provide a suitable alternative, customers will normally receive a refund of payments made to Line Dance Central for services that will no longer be provided.</p>
            <p>Where appropriate, customers may instead be offered the option of transferring payments to an alternative Line Dance Central event.</p>
            <p>Customers will not be required to accept an alternative event or credit instead of a refund where they are legally entitled to a refund.</p>
          </Section>

          {/* Section 18 */}
          <Section number={18} title="Circumstances Beyond Our Reasonable Control">
            <p>Events and transport can occasionally be affected by circumstances outside Line Dance Central's reasonable control.</p>
            <p>These may include severe weather, flooding, fire, transport disruption, road closures, government restrictions, public health emergencies, venue closure, utility failure or other exceptional circumstances.</p>
            <p>Where this occurs, Line Dance Central will make reasonable efforts to:</p>
            <ul>
              <li>provide the event as planned;</li>
              <li>rearrange affected elements; or</li>
              <li>offer an appropriate alternative where reasonably possible.</li>
            </ul>
            <p>Any refund or other remedy will depend upon the circumstances, services that can still be provided, costs already incurred or committed, sums recoverable from suppliers and the customer's statutory rights.</p>
            <p>Nothing in these terms removes rights that cannot legally be excluded.</p>
          </Section>

          {/* Section 19 */}
          <Section number={19} title="Behaviour">
            <p>Guests are expected to behave responsibly and respectfully towards:</p>
            <ul>
              <li>other guests;</li>
              <li>Line Dance Central representatives;</li>
              <li>instructors and entertainers;</li>
              <li>hotel and venue staff;</li>
              <li>coach drivers; and</li>
              <li>members of the public.</li>
            </ul>
            <p>Line Dance Central reserves the right to require a guest to leave the event or coach where their behaviour is seriously disruptive, threatening, abusive, discriminatory or unsafe, or significantly affects the safety or enjoyment of others.</p>
            <p>Where a guest is required to leave because of their own serious misconduct, Line Dance Central will not normally be responsible for resulting accommodation, travel or other costs.</p>
          </Section>

          {/* Section 20 */}
          <Section number={20} title="Damage">
            <p>Guests are responsible for damage they deliberately or negligently cause to hotel, venue, coach or Line Dance Central property.</p>
            <p>The hotel, venue, transport provider or Line Dance Central may seek reasonable reimbursement from the responsible person for damage or additional charges resulting from their actions.</p>
          </Section>

          {/* Section 21 */}
          <Section number={21} title="Health, Safety and Participation">
            <p>Participation in line dancing and other activities is voluntary.</p>
            <p>Guests are responsible for deciding whether an activity is appropriate for them and should follow reasonable safety instructions given by Line Dance Central, instructors, the hotel, venue or coach operator.</p>
            <p>Guests should notify Line Dance Central of accessibility requirements as early as reasonably possible so that reasonable adjustments can be considered.</p>
            <p>Nothing within these terms excludes or restricts liability where doing so would be unlawful, including liability for death or personal injury caused by negligence.</p>
          </Section>

          {/* Section 22 */}
          <Section number={22} title="Dietary, Accessibility and Special Requirements">
            <p>Guests should inform Line Dance Central of dietary requirements, accessibility requirements and other relevant special requests as early as reasonably possible.</p>
            <p>We will make reasonable efforts to accommodate requests.</p>
            <p>Some requirements depend upon the facilities and services provided by the hotel or another third-party supplier and therefore cannot always be guaranteed unless specifically confirmed.</p>
          </Section>

          {/* Section 23 */}
          <Section number={23} title="Personal Belongings">
            <p>Guests remain responsible for their own money, luggage, valuables and personal belongings.</p>
            <p>Line Dance Central cannot accept responsibility for loss, theft or damage except where caused by our negligence or where liability cannot legally be excluded.</p>
          </Section>

          {/* Section 24 */}
          <Section number={24} title="Photography and Filming">
            <p>Photography and filming may take place during Line Dance Central Weekend Events for promotional, social media and historical purposes.</p>
            <p>Guests who do not wish to appear prominently in photographs or video should inform a member of the Line Dance Central team.</p>
            <p>We will make reasonable efforts to respect such requests.</p>
          </Section>

          {/* Section 25 */}
          <Section number={25} title="Travel and Cancellation Insurance" icon={<Shield className="w-5 h-5" />}>
            <p className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-violet-800 mb-4">
              Line Dance Central strongly recommends that guests obtain appropriate travel and cancellation insurance from the date they book.
            </p>
            <p>Customers should consider insurance providing suitable cover for circumstances including:</p>
            <ul>
              <li>illness or injury;</li>
              <li>bereavement;</li>
              <li>inability to travel;</li>
              <li>transport disruption; and</li>
              <li>other personal circumstances preventing attendance.</li>
            </ul>
            <p>A customer's inability to attend does not automatically entitle them to a refund outside these Booking Terms & Conditions.</p>
          </Section>

          {/* Section 26 */}
          <Section number={26} title="Information Provided by Customers">
            <p>The Lead Booker is responsible for ensuring that information supplied to Line Dance Central is accurate.</p>
            <p>This includes names, contact details, room requirements, dietary information, accessibility requirements and other relevant booking information.</p>
            <p>Line Dance Central will not normally be responsible for additional costs resulting directly from incorrect information supplied by the customer.</p>
          </Section>

          {/* Section 27 */}
          <Section number={27} title="Hotel, Venue and Supplier Rules">
            <p>Guests must comply with reasonable rules and requirements imposed by the hotel, venue, coach operator and other suppliers involved in providing the weekend.</p>
            <p>Where relevant, customers will be informed of important supplier requirements affecting their booking.</p>
          </Section>

          {/* Section 28 */}
          <Section number={28} title="Complaints">
            <p>If a problem occurs during the weekend, guests should bring it to the attention of a Line Dance Central representative as soon as reasonably possible.</p>
            <p>This gives us an opportunity to investigate and, where possible, resolve the problem during the event.</p>
            <p>Any complaint following the event should be submitted in writing with sufficient information for Line Dance Central to investigate.</p>
          </Section>

          {/* Section 29 */}
          <Section number={29} title="Consumer Rights">
            <p>Nothing within these Booking Terms & Conditions is intended to remove, restrict or reduce a customer's statutory consumer rights.</p>
            <p>If any individual provision of these terms is found to be invalid or unenforceable, the remaining provisions will continue to apply.</p>
          </Section>

          {/* Section 30 */}
          <Section number={30} title="Governing Law">
            <p>These Booking Terms & Conditions are governed by the laws of England and Wales.</p>
          </Section>

          {/* Section 31 */}
          <Section number={31} title="Acceptance of Booking Terms">
            <p>Payment of the £35 weekend deposit constitutes acceptance of these Booking Terms & Conditions.</p>
            <p>Where optional coach travel is selected, payment of the additional £15 coach deposit also confirms acceptance of the terms relating to coach travel.</p>
            <p>The Lead Booker accepts these terms on behalf of all persons included within their booking and is responsible for ensuring those persons have access to them.</p>
          </Section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-stone-200 text-center">
          <p className="text-stone-500 text-sm">Line Dance Central CIC</p>
          <a href="https://linedancecentral.uk" className="text-violet-600 hover:text-violet-700 transition-colors text-sm">
            www.linedancecentral.uk
          </a>
        </div>

        {/* Back to Top */}
        <div className="mt-8 text-center">
          <Button
            variant="outline"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="border-stone-300 text-stone-600 hover:bg-stone-100"
          >
            Back to Top
          </Button>
        </div>
      </div>
    </div>
  );
};

// Section Component
const Section = ({ number, title, icon, children }) => (
  <div className="bg-stone-50 rounded-xl border border-stone-200 overflow-hidden">
    <div className="bg-stone-50 px-6 py-4 border-b border-stone-200 flex items-center gap-3">
      {icon && <span className="text-violet-600">{icon}</span>}
      <h3 className="text-lg font-semibold text-stone-900">
        <span className="text-violet-600 mr-2">{number}.</span>
        {title}
      </h3>
    </div>
    <div className="px-6 py-4 prose prose-sm max-w-none prose-p:text-stone-600 prose-li:text-stone-600 prose-ul:my-2 prose-p:my-2 prose-strong:text-stone-900">
      {children}
    </div>
  </div>
);

// Cancellation Tier Component
const CancellationTier = ({ period, description, warning }) => (
  <div className="bg-stone-50 rounded-lg p-4 border-l-4 border-violet-500">
    <h5 className="text-stone-900 font-medium mb-2">{period}</h5>
    <p className="text-stone-600 text-sm">{description}</p>
    {warning && (
      <p className="text-amber-700 text-sm mt-2 font-medium">{warning}</p>
    )}
  </div>
);

export default WeekendTermsPage;
